import { useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError, apiFetch, post } from "@/lib/api";
import { API_BASE } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { reportStepFailed } from "./analytics";

/** One task of the SaaS research report (services/agent .../saas-research). */
interface ResearchResult {
  task: string;
  data: Record<string, unknown>;
}

/** The product itself, as the research's product workers found it (saas-research/profile.ts). */
export interface ResearchProfile {
  company: { name: string | null; description: string | null };
  api: {
    hasPublicApi: boolean | null;
    docsUrl: string | null;
    apiReferenceUrl: string | null;
    openApiUrl: string | null;
  };
  entities: Array<{ name: string; description: string | null }>;
  webhooks: Array<{ event: string; description: string | null }>;
  /** Apps its customers would connect it to and why. `prompt`/`trigger` are absent on profiles researched before they existed. */
  suggestedIntegrations?: Array<{ app: string; object: string; reason: string; prompt?: string | null; trigger?: string | null }>;
  exampleUseCase?: { prompt: string; partnerApp: string; trigger: string; sampleResult: string } | null;
}

export type ResearchStepId = "website" | "api_docs" | "entities" | "webhooks";

/** The run's own progress, one entry per row of the research screen. */
export interface ResearchProgress {
  steps: Array<{ id: ResearchStepId; status: "pending" | "running" | "done" | "failed"; finding: string | null }>;
  profileReady: boolean;
}

/** What GET/POST /agent/research/saas answer, unwrapped. */
export type ResearchState =
  | {
      status: "ready";
      domain: string;
      summary: string;
      results: ResearchResult[];
      sources: string[];
      researchedAt: string;
      profile?: ResearchProfile | null;
      progress?: ResearchProgress;
    }
  | { status: "running"; domain: string; pollAfterSeconds?: number; profile?: ResearchProfile | null; progress?: ResearchProgress }
  | { status: "failed"; error: string }
  // A cached report the research no longer stands on (no profile, or too old): started again like a missing one.
  | { status: "stale"; domain: string }
  | { status: "missing" };

/** The research outcome the screens branch on. */
export type ResearchOutcome = "api" | "nodocs" | "nothing";

export interface ProductFindings {
  name: string;
  domain: string;
  /** "What <product> does". Null when the site could not be read. */
  description: string | null;
  /** The source the description came from, for the row's link icon. */
  descriptionSource: string | null;
  apiDocsUrl: string | null;
  entities: string[];
  webhooks: Array<{ name: string; description: string }>;
  outcome: ResearchOutcome;
}

/** "dryrun.security" -> "Dryrun". Used until research names the company. */
export function nameFromDomain(domain: string): string {
  const label = domain.replace(/^www\./, "").split(".")[0] ?? domain;
  return label ? label[0]!.toUpperCase() + label.slice(1) : domain;
}

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
}

/**
 * Docs for the product's own API among the URLs the research read: on the
 * product's domain (or a subdomain of it), and either a docs/developer host or
 * an API/docs path. Anything else is a page ABOUT them, not their docs.
 */
export function findApiDocs(domain: string, urls: readonly string[]): string | null {
  const root = domain.toLowerCase().replace(/^www\./, "");
  const ranked = urls
    .map((u) => ({ u, host: hostOf(u), path: (() => { try { return new URL(u).pathname.toLowerCase(); } catch { return ""; } })() }))
    .filter((x) => x.host && (x.host === root || x.host.endsWith(`.${root}`)))
    .map((x) => {
      const docsHost = /^(docs|developers?|dev|api|reference)\./.test(x.host!);
      const docsPath = /\/(docs|developers?|api|reference|openapi)(\/|$)/.test(x.path);
      return { ...x, score: (docsHost ? 2 : 0) + (docsPath ? 1 : 0) };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  return ranked[0]?.u ?? null;
}

const firstSentence = (s: string): string => {
  const t = s.trim().replace(/\s+/g, " ");
  const m = t.match(/^(.{20,320}?[.!?])(\s|$)/);
  if (m) return m[1]!.replace(/\.$/, "");
  if (t.length <= 320) return t;
  // No sentence end in reach: stop at a word, never mid-word.
  return `${t.slice(0, 320).replace(/\s+\S*$/, "")}…`;
};

const nonEmpty = (v: string | null | undefined): string | null => (v && v.trim() ? v.trim() : null);

/** The product profile, once its inputs have settled; it lands before the market report does. */
export function profileOf(state: ResearchState | undefined): ResearchProfile | null {
  if (!state || (state.status !== "ready" && state.status !== "running")) return null;
  if (state.status === "running" && !state.progress?.profileReady) return null;
  return state.profile ?? null;
}

/**
 * What the research found, in the shape the screens show. The product profile
 * (the API docs, entities and webhooks workers) is read first. Without one
 * (a row researched before profiles existed), the market report can still
 * name the company and say what it does, and its sources can point at API
 * docs, but entities and webhooks start empty for the person to fill in.
 */
export function findingsFromResearch(domain: string, state: ResearchState): ProductFindings {
  const base: ProductFindings = {
    name: nameFromDomain(domain),
    domain,
    description: null,
    descriptionSource: null,
    apiDocsUrl: null,
    entities: [],
    webhooks: [],
    outcome: "nothing",
  };

  const product = profileOf(state);
  if (product) {
    const rawDescription = nonEmpty(product.company?.description);
    const description = rawDescription ? firstSentence(rawDescription) : null;
    const apiDocsUrl =
      nonEmpty(product.api?.docsUrl) ??
      nonEmpty(product.api?.apiReferenceUrl) ??
      nonEmpty(product.api?.openApiUrl) ??
      (state.status === "ready" ? findApiDocs(domain, state.sources) : null);
    return {
      ...base,
      name: nonEmpty(product.company?.name)?.replace(/\.$/, "") ?? base.name,
      description,
      descriptionSource: description ? `https://${domain}` : null,
      apiDocsUrl,
      entities: (product.entities ?? []).map((e) => nonEmpty(e.name)).filter((n): n is string => !!n),
      webhooks: (product.webhooks ?? [])
        .filter((w) => !!nonEmpty(w.event))
        .map((w) => ({ name: w.event.trim(), description: w.description ?? "" })),
      outcome: !description ? "nothing" : apiDocsUrl ? "api" : "nodocs",
    };
  }

  if (state.status !== "ready") return base;

  const profile = state.results.find((r) => r.task === "company_profile")?.data ?? {};
  const name = typeof profile.accountName === "string" && profile.accountName.trim() ? profile.accountName.trim().replace(/\.$/, "") : base.name;
  const rawDescription = typeof profile.description === "string" ? profile.description : "";
  const profileSources = Array.isArray(profile.sources) ? (profile.sources as Array<{ url?: unknown }>) : [];
  const ownSource = profileSources
    .map((s) => (typeof s?.url === "string" ? s.url : null))
    .find((u): u is string => !!u && (hostOf(u) ?? "").endsWith(domain));
  const description = rawDescription.trim() ? firstSentence(rawDescription) : null;
  const apiDocsUrl = findApiDocs(domain, state.sources);

  return {
    ...base,
    name,
    description,
    descriptionSource: description ? (ownSource ?? `https://${domain}`) : null,
    apiDocsUrl,
    outcome: !description ? "nothing" : apiDocsUrl ? "api" : "nodocs",
  };
}

/**
 * Whether a link opens: null while it is being checked, or when there is none.
 * The api fetches it server-side (a browser fetch is blocked cross-origin), so
 * a page that answers 404 or not at all reads as dead. Nothing is shown as a
 * source until it has been seen to open.
 */
export function useLinkWorks(url: string | null | undefined): boolean | null {
  const q = useQuery<boolean>({
    queryKey: ["onboarding", "link-works", url],
    queryFn: async () => {
      try {
        // A dead customer site is a 502 here, and an answer, not a fault.
        await apiFetch(`${API_BASE}/v1/web/fetch`, post({ url, maxChars: 1 }), { reportFailure: false, idempotent: true });
        return true;
      } catch {
        return false;
      }
    },
    enabled: !!url,
    staleTime: Infinity,
    retry: false,
  });
  return url && q.isSuccess ? q.data : null;
}

const researchUrl = (domain?: string) =>
  `${API_BASE}/v1/agent/research/saas${domain ? `/${encodeURIComponent(domain)}` : ""}`;

async function readResearch(domain: string): Promise<ResearchState> {
  try {
    const body = await apiFetch<ResearchState>(researchUrl(domain));
    return body;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { status: "missing" };
    throw err;
  }
}

/**
 * The SaaS research for the product's domain: started once if there is none,
 * then polled until it is ready or failed. A refused start (a public email
 * domain, no AI credits left) reads as failed, which the screens show as
 * "nothing found, so we ask".
 */
export function useProductResearch(domain: string | null, opts?: { start?: boolean }) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const started = useRef<string | null>(null);

  const query = useQuery<ResearchState>({
    queryKey: ["onboarding", "research", orgId, domain],
    queryFn: () => readResearch(domain!),
    enabled: !!orgId && !!domain,
    refetchInterval: (q) => (q.state.data?.status === "running" ? 5_000 : false),
    retry: 1,
  });

  const start = useMutation({
    mutationFn: async (d: string): Promise<ResearchState> => {
      try {
        return await apiFetch<ResearchState>(researchUrl(), post({ domain: d }));
      } catch (err) {
        reportStepFailed("research", 1, "research_start_failed", err);
        const message = err instanceof Error ? err.message : "research could not start";
        return { status: "failed", error: message };
      }
    },
    onSuccess: () => { void query.refetch(); },
  });

  const shouldStart = (opts?.start ?? true) && !!domain && (query.data?.status === "missing" || query.data?.status === "stale");
  useEffect(() => {
    if (!shouldStart || !domain || started.current === domain) return;
    started.current = domain;
    start.mutate(domain);
  }, [shouldStart, domain, start]);

  // A start that failed never shows up on the GET, so its answer stands in for it.
  const state: ResearchState | undefined =
    start.data?.status === "failed" ? start.data : query.data;

  const found = domain && state ? findingsFromResearch(domain, state) : null;
  // API docs found on a page that does not open are not docs anyone can use.
  const docsWork = useLinkWorks(found?.apiDocsUrl);
  const checkingDocs = !!found?.apiDocsUrl && docsWork === null;
  const findings = found && found.apiDocsUrl && docsWork === false
    ? { ...found, apiDocsUrl: null, outcome: found.description ? "nodocs" as const : "nothing" as const }
    : found;
  // The profile is all step 1 needs, so a run whose profile is ready counts as
  // done here although its slower market workers are still going.
  const profileReady = !!profileOf(state);

  return {
    state,
    findings,
    progress: state && (state.status === "ready" || state.status === "running") ? state.progress ?? null : null,
    running: (!profileReady && (!state || state.status === "running" || state.status === "missing" || state.status === "stale" || start.isPending)) || checkingDocs,
    isError: query.isError,
    error: query.error,
  };
}

/** The part of the step 1 product a correction can change (agent .../saas-research/revise.ts). */
export interface RevisableProduct {
  name: string | null;
  description: string | null;
  entities: string[];
  apiDocsUrl: string | null;
  webhooks: Array<{ name: string; description: string }>;
}

export type ReviseField = "all" | "what" | "core" | "api" | "webhooks";

/** An earlier correction in the same panel, sent so a follow-up can refer back to it. */
export interface ReviseTurn {
  ask: string;
  outcome: "kept" | "undone" | "proposed" | "nothing" | "failed";
  changed: Array<keyof RevisableProduct>;
  note: string | null;
}

export interface ProductRevision {
  product: RevisableProduct;
  changed: Array<keyof RevisableProduct>;
  note: string;
}

/** Applies a correction in the person's own words and returns the proposal. Nothing is saved until they keep it. */
export function useReviseProduct() {
  return useMutation({
    mutationFn: (body: { domain: string | null; field: ReviseField; instruction: string; product: RevisableProduct; history: ReviseTurn[] }) =>
      apiFetch<ProductRevision>(`${API_BASE}/v1/agent/research/product/revise`, post(body)),
  });
}
